import { integer, real, sqliteTable, text } from 'drizzle-orm/sqlite-core';

/**
 * The local projection of `boat_details`.
 *
 * These columns mirror the `boat_details` query in `sync-streams.yaml` one for one — all 47 of
 * them. The stream has always sent the whole set; the table only declared seven of them, so
 * everything else arrived on the device and was never projected into the view. Declaring a column
 * here is what makes an already-synced value readable, which is why widening this needs no
 * re-download: PowerSync keeps the row data and rebuilds the view.
 *
 * Keep this list and the stream's list equal in both directions. A column declared here that the
 * stream does not send is permanently null and reads as missing data rather than as a mistake —
 * `strapi_ref_document_id` is the one to watch, since it exists on the table and is what the
 * legacy GraphQL forms branch on, but the stream has never selected it.
 *
 * Types follow the Postgres source in `boat_details.sql`: `numeric` and `float8` become `real`,
 * every `int8` becomes `integer`, `bool` becomes `integer` because SQLite has no boolean, and
 * dates and timestamps stay `text`.
 */
export const boat_details = sqliteTable('boat_details', {
  id: text('id').notNull().primaryKey(),
  laravel_id: integer('laravel_id'),

  // Identity
  name: text('name'),
  description: text('description'),
  flag: text('flag'),
  year: integer('year'),
  hull_sn: text('hull_sn'),

  /**
   * The cover photo, as both the id the client writes and the uuid the upsert takes.
   *
   * The same pair `trips` carries, and for the same reason: an upload hands back a Laravel integer
   * id, while `PUT /api/boat_details` accepts `image_uuid`. The client writes `image_id` and the
   * server resolves it; `image_uuid` comes back down the stream so a synced boat can be read
   * without a second lookup.
   */
  image_id: integer('image_id'),
  image_uuid: text('image_uuid'),
  // `boat_id` is still a Laravel integer id with no uuid on the stream — a model link has to bridge
  // that itself.
  boat_id: integer('boat_id'),
  strapi_ref_id: integer('strapi_ref_id'),
  /**
   * The model this boat is, as `PUT /api/boat_details` takes it: a Sanity document id for a
   * catalogue model, or a `boat_models` uuid for one a user submitted.
   *
   * Not a uuid column and not a foreign key — the catalogue half of the data lives in Sanity and
   * has no row here, so a boat referencing one carries whatever id Sanity generated.
   */
  boat_model_ref: text('boat_model_ref'),

  // Ownership and purchase
  acquisition_date: text('acquisition_date'),
  /** `'new'` or `'used'`; the table constrains it to those two, or null. */
  acquisition_type: text('acquisition_type'),
  nr_past_owners: integer('nr_past_owners'),
  /** Ordinal text as stored — `'2nd'`, not `2`. */
  ownership_order: text('ownership_order'),
  price: real('price'),
  /** ISO 4217, three characters. */
  currency: text('currency'),
  is_vat_paid: integer('is_vat_paid'),

  // Location
  /** `"<lat>,<lng>"`, written to eight decimal places. */
  location: text('location'),
  location_toponym: text('location_toponym'),

  // Communications and safety
  mmsi: text('mmsi'),
  callsign: text('callsign'),
  epirb: text('epirb'),
  in_reach_tracker_id: text('in_reach_tracker_id'),
  spot_tracker_id: text('spot_tracker_id'),
  satellite_phone: text('satellite_phone'),
  mobile_phone: text('mobile_phone'),

  // Electrical system. Voltages are one of 12, 24 or 48; capacities are in Ah and the rest in W.
  engine_battery_voltage: integer('engine_battery_voltage'),
  engine_battery_capacity: integer('engine_battery_capacity'),
  service_battery_voltage: integer('service_battery_voltage'),
  service_battery_capacity: integer('service_battery_capacity'),
  /** Marked "to be removed" on the Postgres table. Declared because the stream sends it. */
  battery_power: integer('battery_power'),
  inverter_power: integer('inverter_power'),
  solar_power: integer('solar_power'),
  wind_power: integer('wind_power'),
  dg_power: integer('dg_power'),
  service_loads: integer('service_loads'),

  // Tender
  tender_name: text('tender_name'),
  tender_vendor: text('tender_vendor'),
  tender_model: text('tender_model'),
  /** Stored in feet whatever unit the user typed in. */
  tender_length: real('tender_length'),

  // State
  status: integer('status'),
  enabled: integer('enabled'),
  /** `0` while a sync error is open on the boat; not the same thing as the boat being switched off. */
  is_enabled: integer('is_enabled'),
  /**
   * Which licence the boat holds. Already in the sync stream; declared here because a boat with no
   * licence counts as disabled — see `isBoatDisabled`.
   */
  subscription_id: integer('subscription_id'),

  organization_id: text('organization_id'),

  // Bookkeeping
  /** Free-form JSON carried over from an older shape of this table. */
  old_fields: text('old_fields'),
  created_at: text('created_at'),
  updated_at: text('updated_at'),
  /** Always null locally: the stream filters soft-deleted rows out. Do not filter on it. */
  deleted_at: text('deleted_at'),
});
